import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { useSelector } from "react-redux";
import { CircularProgress, Popper, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import theme from "../../../theme";
import Button from "../../ui/Button";
import { Brand } from "../../../interfaces/Brand";
import {
  fetchAssemblyGroups,
  fetchBrands,
  fetchLocalBrands,
} from "../../../services/tecdocService";
import { AssemblyGroup } from "../../../interfaces/AssemblyGroup";
import { LoadingButton } from "@mui/lab";
import { LinkingTargetState } from "../../../store/reducers/linkingTargetReducer";
import { LocalBrand } from "../../../interfaces/LocalBrand";

const Item = styled(Paper)(({ theme }) => ({
  boxShadow: "none !important",
  borderRadius: 0,
  backgroundColor: "#F1F1F1 !important",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const BrandProductGroupSearch = () => {
  const linkingTargetType = useSelector(
    (state: { linkingTarget: LinkingTargetState }) =>
      state.linkingTarget.linkingTargetType
  );

  const [openBrands, setOpenBrands] = React.useState(false);
  const [brands, setBrands] = React.useState<readonly LocalBrand[]>([]);
  const [selectBrand, setSelectBrand] = React.useState<LocalBrand | null>(null);
  const [errorBrand, setErrorBrand] = React.useState<string | null>(null);
  const loadingBrands = openBrands && brands.length === 0;

  const [isLoadingData, setIsLoadingData] = React.useState(false);

  // Autocomplete for Search by brand
  React.useEffect(() => {
    let activeBrand = true;

    if (!loadingBrands) {
      return undefined;
    }

    (async () => {
      try {
        if (activeBrand) {
          const data = await fetchLocalBrands();
          setBrands(data || []);
          setErrorBrand(null);
        }
      } catch (error) {
        console.error("Error fetching Brands:", error);
        setErrorBrand("Not able to fetch brands, Please try again.");
      }
    })();

    return () => {
      activeBrand = false;
    };
  }, [loadingBrands]);

  const handleBrands = async (
    event: React.ChangeEvent<{}>,
    brand: LocalBrand | null
  ) => {
    setSelectBrand(brand);
  };


  React.useEffect(() => {

    if (!openBrands) {
      setBrands([]);
    }
  }, [openBrands, selectBrand]);


  const handleSubmitBrandData = () => {
    setIsLoadingData(true);
    const dataSupplierId = selectBrand?.Shifra;
    if (dataSupplierId ) {
      const url = `/search-internal-by-brand?brand=${dataSupplierId}`;
      window.location.href = url;
      setIsLoadingData(false);
    }

  };

  return (
    <Box sx={{ paddingLeft: 4, paddingRight: 4 }}>
      <Item
        sx={{
          display: "flex",
          alignItems: "center",
          paddingBottom: "0 !important",
        }}
      >
        <img
          src="/search_plus.svg"
          alt="Search car icon"
          style={{ width: "30px", marginRight: "8px" }}
        />
        <Typography variant="body1" align="left" sx={{ fontSize: "13px" }}>
          Search by brands and product groups
        </Typography>
      </Item>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          gap: 0,
          padding: { xs: 0, sm: 0, md: 1 },
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.dark,
            width: "40px",
            minWidth: "40px",
            fontSize: "1.2rem",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          1
        </Box>
        <Box sx={{ flex: 1, width: "100%", paddingRight: 2 }}>
          <Autocomplete
            disablePortal
            id="brands"
            open={openBrands}
            onOpen={() => {
              setOpenBrands(true);
            }}
            onClose={() => {
              setOpenBrands(false);
            }}
            ListboxProps={{
              style: { maxHeight: 200, overflowY: "auto" },
            }}
            PaperComponent={(props) => {
              const anchorEl = document.getElementById("brands");
              return (
                <Popper
                  {...props}
                  open={openBrands}
                  anchorEl={anchorEl}
                  placement="bottom-start"
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [-7, -10],
                      },
                    },
                    {
                      name: "matchWidth",
                      enabled: true,
                      phase: "beforeWrite",
                      requires: ["computeStyles"],
                      fn: ({ state }) => {
                        // Set the width based on the reference element's width
                        state.styles.popper.width = `${
                          (
                            state.elements.reference as HTMLElement
                          ).getBoundingClientRect().width + 40
                        }px`;
                      },

                      effect: ({ state }) => {
                        // Set the width in the effect phase
                        const reference = state.elements
                          .reference as HTMLElement;
                        state.elements.popper.style.width = `${
                          reference.getBoundingClientRect().width
                        }px`;

                      },
                    },
                  ]}
                  style={{
                    zIndex: 9999,
                    fontSize: "13px",
                    fontFamily: "Inter",
                    border: "1px solid #f1f1f1",
                    boxShadow: "2px 3px 3px #ccc",
                    position: "relative",
                    background: "#fff",
                  }}
                />
              );
            }}
            isOptionEqualToValue={(option, value) =>
              option.Emertimi === value.Emertimi
            }
            getOptionLabel={(option) => option.Emertimi}
            options={brands}
            loading={loadingBrands}

            sx={{
              width: "100%",
              backgroundColor: "#fff",
              "& .MuiAutocomplete-inputRoot": {
                width: "100%",
              },
              ".MuiInputBase-input": {
                height: "40px",
                marginTop: "-15px",
                marginBottom: "0px",
                paddingTop: "0px",
                paddingBottom: "0px",
              },
            }}
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{ 
                  p: 1,
                  fontSize: '13px',
                  fontFamily: "Inter"
                }}
                {...props}
              >
                {option.Emertimi}
              </Box>
            )}
            onChange={handleBrands}

            renderInput={(params) => (
              <TextField
                {...params}
                label="Brands"
                InputLabelProps={{
                  ...params.InputLabelProps,
                  sx: {
                    color: theme.palette.text.primary,
                    fontSize: "13px",
                    fontFamily: "Inter",
                    height: "100%",
                    top: "-6px",
                    transform: "translate(24px, 12px), scale(1) !important",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    borderRadius: 0,
                    height: "40px",
                    fontSize: "13px",
                    fontFamily: "Inter",
                    width: "100%", // Add this
                  },
                  endAdornment: (
                    <React.Fragment>
                      {loadingBrands ? (
                        <CircularProgress
                          color="inherit"
                          size={15}
                          sx={{ marginTop: "-15px" }}
                        />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Box>

      <Box flex={1}>
        {isLoadingData ? (
          <LoadingButton
            loading
            variant="contained"
            sx={{
              height: "38px",
              borderRadius: 0,
              width: "100%",
              alignItems: "center", // Center items vertically
              justifyContent: "flex-end",
            }}
          >
            Submit
          </LoadingButton>
        ) : (
          <>
            <Button
              sx={{
                width: "100%",
                mt: 1,
                borderRadius: 0,
                textTransform: "capitalize",
                backgroundColor: "primary.dark",
                color: "text.secondary",
              }}
              onClick={handleSubmitBrandData}
              disabled={!selectBrand}
            >
              Search
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default BrandProductGroupSearch;
